import React from "react";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Link } from "react-router-dom";
import { Section, Kicker, SerifTitle, HairlineBrass } from "@/components/Section";
import { EditorialImage } from "@/components/EditorialImage";
import { MediaGallery } from "@/components/MediaGallery";
import { HeroVideo, heroTextShadow } from "@/components/HeroVideo";
import { Separator } from "@/components/ui/separator";
import { ArrowUpRight, Check } from "lucide-react";
import {
    STUDIOS,
    REVIEWS,
    BRAND_BUSINESS_PACKAGES,
    BRAND_BUSINESS_ADDONS,
    CUSTOM_BUSINESS_SOLUTIONS,
    BUSINESS_HERO,
    BUSINESS_VISUAL_PROMISES,
    BUSINESS_BUYER_TYPES,
    BUSINESS_IMAGE_USE_CASES,
    BUSINESS_GUIDED_EXPERIENCE,
    BUSINESS_TRUST_CTA,
    PRICING_REASSURANCE,
    PRICING_CUSTOM_NOTE,
    PRICING_BOOKING_NOTE,
} from "@/lib/data";
import { usePortfolio } from "@/lib/content";
import InvestmentSection, { AddOnsSection, CustomSolutions } from "@/components/Investment";

export default function BrandBusiness() {
    const s = STUDIOS.brand;
    const PORTFOLIO = usePortfolio();
    const work = PORTFOLIO.filter((p) => ["Branding", "Business", "Editorial"].includes(p.category)).slice(0, 8);
    const brandReviews = [REVIEWS[0], REVIEWS[2], REVIEWS[5]].filter(Boolean);

    return (
        <div className="relative">
            <Seo {...SEO.brandBusiness} />
            {/* HERO — brand-tone video, copy preserved */}
            <HeroVideo
                dataTestId="brand-hero"
                ariaLabel="Brand & Business Studio — editorial brand photography by Elizaveta von Stuben Imagery."
            >
                <span
                    className="block font-sans uppercase text-[11px] sm:text-[12px]"
                    style={{ color: "#e6e1db", letterSpacing: "0.22em", ...heroTextShadow.light }}
                >
                    {BUSINESS_HERO.kicker}
                </span>
                <h1
                    className="font-serif-display mt-3 sm:mt-4 text-4xl sm:text-6xl lg:text-[64px] leading-[1.02] max-w-[22ch]"
                    data-testid="brand-hero-headline"
                    style={{ color: "#fbf8f4", ...heroTextShadow.heavy }}
                >
                    {BUSINESS_HERO.headline}
                </h1>
                <p
                    className="mt-5 max-w-[68ch] text-sm sm:text-base leading-[1.75]"
                    style={{ color: "#f5f1eb", ...heroTextShadow.medium }}
                >
                    {BUSINESS_HERO.subhead}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/inquire?type=Brand%20Story%20Session" data-testid="brand-primary-cta" className="btn-request">
                        {BUSINESS_HERO.ctaPrimary}
                    </Link>
                    <Link to="/portfolio/brand-business" data-testid="brand-secondary-cta" className="btn-request btn-request--sm btn-request--outline-light">
                        {BUSINESS_HERO.ctaSecondary}
                    </Link>
                </div>
                <div className="mt-5">
                    <Link
                        to="/business"
                        data-testid="brand-view-arizona-packages"
                        className="inline-flex items-center text-[11px] tracking-[0.22em] uppercase transition-colors lux-underline"
                        style={{ color: "rgba(251,248,244,0.7)" }}
                    >
                        View Arizona Business Packages <ArrowUpRight className="ml-2 h-3.5 w-3.5" />
                    </Link>
                </div>
            </HeroVideo>

            {/* WHAT STRONG BUSINESS VISUALS SHOULD DO */}
            <Section spacing="loose" data-testid="business-visual-promises">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
                    <div className="lg:col-span-7">
                        <Kicker>Why this matters</Kicker>
                        <SerifTitle className="mt-3">What strong business visuals should do.</SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[48ch]">
                        Business clients do not buy beautiful photos. They buy trust, credibility, and visuals that help them attract and convert clients.
                    </p>
                </div>
                <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5">
                    {BUSINESS_VISUAL_PROMISES.map((p) => (
                        <li
                            key={p.n}
                            data-testid={`business-promise-${p.n}`}
                            className="bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] p-6 flex flex-col h-full"
                        >
                            <div className="kicker">{p.n}</div>
                            <h3 className="font-serif-display text-lg leading-tight mt-2">{p.title}</h3>
                            <Separator className="my-4 bg-[hsl(var(--lux-brass))]/45 w-8" />
                            <p className="text-[13px] leading-[1.7] text-muted-foreground">{p.copy}</p>
                        </li>
                    ))}
                </ul>
            </Section>

            {/* WHO THIS IS FOR */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border" data-testid="business-buyer-types">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
                    <div className="lg:col-span-7">
                        <Kicker>Who this is for</Kicker>
                        <SerifTitle className="mt-3">Built for the businesses we work with most.</SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[48ch]">
                        If you see yourself below, the studio is built for you. Each engagement is tailored to your industry, audience, and growth stage.
                    </p>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-x-12 gap-y-1">
                    {BUSINESS_BUYER_TYPES.map((t) => (
                        <li key={t} className="flex items-start gap-3 py-3 border-b border-border last:border-b-0">
                            <Check className="h-4 w-4 mt-1 text-[hsl(var(--lux-brass))] shrink-0" />
                            <span className="text-sm text-foreground/90">{t}</span>
                        </li>
                    ))}
                </ul>
            </Section>

            {/* INVESTMENT — softer summary; full promo pricing lives on /business */}
            <Section spacing="normal" className="bg-background border-t border-border" data-testid="bb-investment-summary">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <Kicker>Investment</Kicker>
                        <SerifTitle className="mt-3">A simple, premium menu of business sessions.</SerifTitle>
                        <p className="mt-5 text-sm leading-[1.85] text-muted-foreground max-w-[60ch]">
                            From a polished professional headshot to a focused content production day — every session is custom quoted based on scope, deliverables, and where the images will be used.
                        </p>
                        <ul className="mt-7 divide-y divide-border border-y border-border max-w-[640px]">
                            {BRAND_BUSINESS_PACKAGES.map((p) => (
                                <li key={p.id} className="py-4 flex items-baseline justify-between gap-4">
                                    <div>
                                        <div className="font-serif-display text-lg leading-tight">{p.name}</div>
                                        <div className="text-xs text-muted-foreground mt-1">{p.timing}</div>
                                    </div>
                                    <div className="text-[11px] tracking-[0.16em] uppercase text-[hsl(var(--lux-oxblood))] whitespace-nowrap">
                                        {p.starting}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="bg-[hsl(var(--lux-porcelain))] border border-[hsl(var(--lux-oxblood))]/30 rounded-[3px] p-7 sm:p-8">
                            <div className="kicker text-[hsl(var(--lux-oxblood))]">Limited · Summer Promo</div>
                            <h3 className="font-serif-display text-2xl leading-tight mt-3">
                                Arizona Business Packages with promo pricing.
                            </h3>
                            <p className="mt-3 text-sm leading-[1.8] text-muted-foreground">
                                Detailed promo packages — Business Refresh, Brand Content Day, and Brand Content + Video — are listed on the Arizona business page with promo pricing through June 18, 2026.
                            </p>
                            <Link
                                to="/business"
                                data-testid="bb-investment-cta-business"
                                className="btn-request btn-request--sm mt-6 inline-flex"
                            >
                                View Arizona Business Packages
                            </Link>
                            <p className="mt-4 text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                                Custom quotes available on request.
                            </p>
                        </div>
                    </div>
                </div>
            </Section>

            {/* WHAT YOU CAN USE THE IMAGES FOR */}
            <Section spacing="normal" className="border-t border-border" data-testid="business-use-cases">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
                    <div className="lg:col-span-7">
                        <Kicker>Practical value</Kicker>
                        <SerifTitle className="mt-3">What you can use the images for.</SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[50ch]">
                        Every session is planned so the final images are ready to ship — on your site, in your ads, across your social channels, and into client conversations.
                    </p>
                </div>
                <ul className="flex flex-wrap gap-2.5">
                    {BUSINESS_IMAGE_USE_CASES.map((u) => (
                        <li
                            key={u}
                            data-testid={`business-use-case-${u.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="px-4 h-10 inline-flex items-center rounded-full border border-border bg-[hsl(var(--lux-porcelain))] text-[12px] tracking-[0.04em] text-foreground/85"
                        >
                            {u}
                        </li>
                    ))}
                </ul>
            </Section>

            {/* A GUIDED BUSINESS CONTENT EXPERIENCE */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border" data-testid="business-guided-experience">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <Kicker>A guided experience</Kicker>
                        <SerifTitle className="mt-3">{BUSINESS_GUIDED_EXPERIENCE.headline}</SerifTitle>
                        <HairlineBrass className="mt-6" />
                        <p className="mt-6 text-base leading-[1.9] text-foreground/85 max-w-[58ch]">
                            {BUSINESS_GUIDED_EXPERIENCE.copy}
                        </p>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <ul className="space-y-3">
                            {BUSINESS_GUIDED_EXPERIENCE.bullets.map((b) => (
                                <li key={b} className="flex items-start gap-3 bg-background border border-border rounded-[2px] px-5 py-4">
                                    <Check className="h-4 w-4 mt-1 text-[hsl(var(--lux-brass))] shrink-0" />
                                    <span className="text-sm">{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>

            {/* SELECTED WORK */}
            <Section spacing="normal" className="border-t border-border">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <Kicker>Selected Work</Kicker>
                        <SerifTitle className="mt-3">Founders &amp; brands we have shaped.</SerifTitle>
                    </div>
                    <Link to="/portfolio/brand-business" className="hidden sm:inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline">
                        Open Brand &amp; Business archive <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                </div>
                <MediaGallery
                    items={work.map((p, i) => ({
                        id: p.id,
                        src: p.imageHome || p.image,
                        alt: p.alt || p.title,
                        caption: p.title,
                        ratio: i % 2 === 0 ? 4 / 5 : 1,
                    }))}
                    desktopColumns={4}
                    initialCount={work.length}
                    defaultInquiryType="Brand Story Session"
                    galleryId="brand-selected-work"
                />
                <div className="mt-8 sm:hidden">
                    <Link to="/portfolio/brand-business" className="inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline">
                        Open Brand &amp; Business archive <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                </div>
            </Section>

            {/* REVIEWS */}
            <Section spacing="normal">
                <Kicker>From clients</Kicker>
                <SerifTitle className="mt-3">In their own words.</SerifTitle>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {brandReviews.map((r) => (
                        <article key={r.id} className="bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] p-7">
                            <p className="font-serif-display text-lg leading-[1.45]">&ldquo;{r.quote}&rdquo;</p>
                            <Separator className="my-5 bg-[hsl(var(--lux-brass))]/50 w-8" />
                            <div className="text-xs tracking-[0.16em] uppercase">{r.name}</div>
                            <div className="text-xs text-muted-foreground mt-1">{r.type}</div>
                        </article>
                    ))}
                </div>
            </Section>

            {/* FINAL TRUST CTA */}
            <Section spacing="normal" className="text-center bg-[hsl(var(--lux-porcelain))] border-t border-border" data-testid="business-trust-cta">
                <SerifTitle className="max-w-[28ch] mx-auto">{BUSINESS_TRUST_CTA.headline}</SerifTitle>
                <p className="mt-5 text-sm text-muted-foreground max-w-[62ch] mx-auto leading-[1.85]">
                    {BUSINESS_TRUST_CTA.copy}
                </p>
                <Link
                    to={`/inquire?type=${encodeURIComponent(BUSINESS_TRUST_CTA.sessionType)}`}
                    data-testid="business-trust-cta-button"
                    className="btn-request mt-7 inline-flex"
                >
                    {BUSINESS_TRUST_CTA.cta}
                </Link>
            </Section>
        </div>
    );
}
